"use client";

import { Icon24Mic } from "immigram-ui-cmon/src/components/Icons";
import { showNotification } from "immigram-ui-cmon/src/components/Notification/Notification";
import progressSvg from "immigram-ui-cmon/src/svg/progress";
// @ts-expect-error - mic-recorder-to-mp3 doesn't have type definitions
import MicRecorder from "mic-recorder-to-mp3";
import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { parseProseMirrorSchema } from "@/lib/proseMirror";

const MAX_RECORDING_DURATION = 3 * 60 * 1000; // 3 minutes in milliseconds
const MIN_RECORDING_DURATION = 1000; // Minimum 1 second to avoid accidental clicks
// Для транскрибации используем тот же порог, что и для защиты от случайных кликов:
// запись должна длиться хотя бы 1 секунду, но может быть и коротким словом.
const MIN_RECORDING_DURATION_FOR_TRANSCRIPTION = MIN_RECORDING_DURATION;
const MIN_TRANSCRIBED_TEXT_LENGTH = 3; // Minimum 3 characters - allow single words, but filter out very short noise

interface VoiceRecorderButtonProps {
  onTranscriptionComplete: (text: string) => void;
  disabled?: boolean;
  currentValue?: string;
  onTranscribingChange?: (isTranscribing: boolean) => void;
  fieldName?: string;
}

export function VoiceRecorderButton({
  onTranscriptionComplete,
  disabled = false,
  currentValue = "",
  onTranscribingChange,
  fieldName,
}: VoiceRecorderButtonProps) {
  const { t } = useTranslation();
  const recorder = useMemo(() => new MicRecorder({ bitRate: 128 }), []);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const recordingStartTimeRef = useRef<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleStopRecordingRef = useRef<(() => Promise<void>) | null>(null);

  const handleStartRecording = useCallback(async () => {
    if (disabled || isRecording) return;

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      recorder
        .start()
        .then(() => {
          setIsRecording(true);
          recordingStartTimeRef.current = Date.now();

          // Auto-stop after 3 minutes
          timeoutRef.current = setTimeout(() => {
            if (handleStopRecordingRef.current) {
              handleStopRecordingRef.current();
            }
          }, MAX_RECORDING_DURATION);
        })
        .catch((error: unknown) => {
          console.error("Failed to start recording:", error);
          showNotification({
            variant: "alert",
            title:
              t("application:voiceRecorder.error.startRecording") ||
              "Failed to start recording",
          });
        });
    } catch (error: unknown) {
      console.error("Failed to get user media:", error);
      showNotification({
        variant: "alert",
        title:
          t("application:voiceRecorder.error.startRecording") ||
          "Failed to start recording",
      });
    }
  }, [recorder, disabled, isRecording, t]);

  const handleStopRecording = useCallback(async () => {
    if (!isRecording) return;

    // Проверяем минимальную длительность записи
    const recordingDuration = recordingStartTimeRef.current
      ? Date.now() - recordingStartTimeRef.current
      : 0;

    if (recordingDuration < MIN_RECORDING_DURATION) {
      // Запись слишком короткая, просто останавливаем без отправки
      console.log("Recording too short, cancelling:", recordingDuration, "ms");
      try {
        recorder
          .stop()
          .getMp3()
          .catch(() => {
            // Ignore errors when cancelling short recording
          });
      } catch (error: unknown) {
        // Ignore errors when cancelling short recording
      }
      setIsRecording(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      recordingStartTimeRef.current = null;
      return;
    }

    // Если запись слишком короткая для транскрибации, не отправляем на транскрибацию
    if (recordingDuration < MIN_RECORDING_DURATION_FOR_TRANSCRIPTION) {
      console.log(
        "Recording too short for reliable transcription, cancelling:",
        recordingDuration,
        "ms"
      );
      try {
        recorder
          .stop()
          .getMp3()
          .catch(() => {
            // Ignore errors when cancelling short recording
          });
      } catch (error: unknown) {
        // Ignore errors when cancelling short recording
      }
      setIsRecording(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      recordingStartTimeRef.current = null;
      return;
    }

    setIsRecording(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    try {
      // recorder.stop() returns an object with getMp3() method, not a Promise
      const [buffer, blob] = await recorder.stop().getMp3();

      // Проверяем размер файла перед отправкой
      if (buffer.byteLength < 1000) {
        console.warn(
          "Audio file too small, cancelling transcription:",
          buffer.byteLength,
          "bytes"
        );
        showNotification({
          variant: "alert",
          title:
            t("application:voiceRecorder.error.tooShort") ||
            "Recording is too short. Please record for at least 1 second.",
        });
        recordingStartTimeRef.current = null;
        return;
      }

      const file = new File(buffer, "record.mp3", {
        type: blob.type,
        lastModified: Date.now(),
      });

      setIsTranscribing(true);
      onTranscribingChange?.(true);

      // Определяем активность из URL
      const pathname = window.location.pathname;
      let activity: string | undefined;
      if (pathname.includes("/external-profile")) {
        activity = "external";
      } else if (
        pathname.includes("/professional-journey") ||
        pathname.includes("/prof-journey")
      ) {
        activity = "prof journey";
      } else if (pathname.includes("/referees")) {
        activity = "referees";
      }

      // Извлекаем applicationId из URL
      const applicationIdMatch = pathname.match(/\/(gtv|o1)\/([^/]+)/);
      const applicationId = applicationIdMatch
        ? applicationIdMatch[2]
        : undefined;

      const formData = new FormData();
      formData.append("audio", file);
      if (applicationId) {
        formData.append("applicationId", applicationId);
      }
      if (fieldName) {
        formData.append("fieldName", fieldName);
      }
      if (activity) {
        formData.append("activity", activity);
      }

      const response = await fetch("/api/whisper/transcribe", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      const transcribedText = data.text || "";

      console.log("Transcription received:", {
        text: transcribedText,
        length: transcribedText.length,
      });

      // Проверяем длительность записи перед обработкой результата
      const recordingDuration = recordingStartTimeRef.current
        ? Date.now() - recordingStartTimeRef.current
        : 0;

      console.log("Recording duration:", recordingDuration, "ms");

      // Если запись была слишком короткой, игнорируем результат
      if (recordingDuration < MIN_RECORDING_DURATION_FOR_TRANSCRIPTION) {
        console.log(
          "⚠️ Recording too short for reliable transcription, ignoring result:",
          {
            duration: recordingDuration,
            minRequired: MIN_RECORDING_DURATION_FOR_TRANSCRIPTION,
            text: transcribedText,
          }
        );
        recordingStartTimeRef.current = null;
        return;
      }

      // Проверяем, что транскрибированный текст не пустой и имеет минимальную длину
      // Убираем пробелы и проверяем длину
      const trimmedText = transcribedText.trim();

      // Проверяем на странные символы, которые могут появиться при ошибках парсинга
      const hasStrangeSymbols = /[[\]{}]/.test(trimmedText);

      // Проверяем на повторяющиеся фразы (например, "Short description. Short description.")
      const words = trimmedText
        .toLowerCase()
        .split(/\s+/)
        .filter((w: string) => w.length > 0);
      const uniqueWords = new Set(words);
      const repetitionRatio =
        words.length > 0 ? uniqueWords.size / words.length : 0;
      const hasExcessiveRepetition = words.length > 5 && repetitionRatio < 0.4; // Если более 60% слов повторяются

      // Проверяем, что текст содержит хотя бы одну букву или цифру (не только знаки препинания)
      // Ограничиваемся латиницей, цифрами и кириллицей; тексты только на других скриптах (например, японский/китайский)
      // считаем шумом, чтобы отфильтровать случайные фразы от внешних видео
      const hasMeaningfulContent = /[a-zA-Z0-9\u00C0-\u017F\u0400-\u04FF]/.test(
        trimmedText
      );

      // Дополнительная проверка: если текст слишком короткий или состоит только из общих фраз,
      // вероятно это фоновый шум
      const commonNoisePhrases = [
        /^(thank you|thanks|hello|hi|hey|yes|no|ok|okay|yeah|yep|nope|bye)$/i,
        /^(mbc|mbc 뉴스|뉴스 이덕영|이덕영입니다)/i, // Корейские фразы, которые часто появляются при шуме
        /thank you for watching/i, // Часто появляется в фоновом шуме
        /share this video with your friends/i, // Типичная фраза из YouTube-видео
        /dont forget to (like|subscribe)/i,
        /please subscribe to (my|our)? channel/i,
        /дякую за перегляд!?$/i, // Украинская фраза из фонового шума
      ];

      const isCommonNoisePhrase = commonNoisePhrases.some((regex) =>
        regex.test(trimmedText)
      );

      // Эвристика: если запись очень короткая, а текста слишком много, считаем это шумом.
      // Например, 1–2 секунды записи и при этом целое предложение из 6+ слов.
      const isSuspiciousLongTextForDuration =
        (words.length >= 6 && recordingDuration < 2000) ||
        (words.length >= 12 && recordingDuration < 4000);

      // Эвристика: кулинарные/температурные инструкции (часто «подхватываются» с TV/YouTube)
      const looksLikeCookingInstruction =
        /\d+\s*(°c|°f)/i.test(trimmedText) && /\bmin\b/i.test(trimmedText);

      // Если текст очень короткий (меньше 3 символов), отфильтровываем
      // Если текст совпадает с известными шумовыми фразами (isCommonNoisePhrase), отфильтровываем вне зависимости от длины
      // Если текст длиннее 10 символов, но имеет проблемы (странные символы, повторения),
      // или явно выглядит как «подсмотренная» инструкция (кулинария, длинная фраза за очень короткое время) — отфильтровываем
      const isLikelyNoise =
        trimmedText.length < MIN_TRANSCRIBED_TEXT_LENGTH ||
        !hasMeaningfulContent ||
        hasStrangeSymbols ||
        hasExcessiveRepetition ||
        isCommonNoisePhrase ||
        isSuspiciousLongTextForDuration ||
        looksLikeCookingInstruction;

      if (trimmedText && !isLikelyNoise) {
        // Извлекаем текст из существующего значения, если оно в формате JSON ProseMirror
        let existingText = "";
        if (currentValue) {
          // Используем parseProseMirrorSchema для извлечения текста из JSON ProseMirror
          // Эта функция автоматически обрабатывает как JSON, так и обычный текст
          existingText = parseProseMirrorSchema(currentValue);
        }

        // Добавляем новый текст к существующему
        const newText = existingText
          ? `${existingText} ${trimmedText}`
          : trimmedText;
        console.log("✅ Transcription accepted, adding to field:", trimmedText);
        onTranscriptionComplete(newText);
      } else {
        // Текст слишком короткий, не содержит значимого контента или является шумом
        console.log("⚠️ Transcribed text ignored (likely noise):", {
          text: trimmedText,
          length: trimmedText.length,
          minRequired: MIN_TRANSCRIBED_TEXT_LENGTH,
          hasContent: hasMeaningfulContent,
          hasStrangeSymbols,
          hasExcessiveRepetition,
          isCommonNoisePhrase,
          duration: recordingDuration,
          reasons: {
            tooShort: trimmedText.length < MIN_TRANSCRIBED_TEXT_LENGTH,
            noContent: !hasMeaningfulContent,
            strangeSymbols: hasStrangeSymbols,
            excessiveRepetition: hasExcessiveRepetition,
            commonNoise: trimmedText.length < 30 && isCommonNoisePhrase,
          },
        });
        // Не показываем уведомление, просто игнорируем результат
      }
    } catch (error: unknown) {
      console.error("Transcription error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("application:voiceRecorder.error.transcription") ||
            "Failed to transcribe audio";
      showNotification({
        variant: "alert",
        title: errorMessage,
      });
    } finally {
      setIsTranscribing(false);
      onTranscribingChange?.(false);
      recordingStartTimeRef.current = null;
    }
  }, [
    recorder,
    isRecording,
    currentValue,
    onTranscriptionComplete,
    onTranscribingChange,
    fieldName,
    t,
  ]);

  // Store handleStopRecording in ref to avoid circular dependencies
  useEffect(() => {
    handleStopRecordingRef.current = handleStopRecording;
  }, [handleStopRecording]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Cleanup: stop recording if still recording
      if (isRecording) {
        try {
          recorder
            .stop()
            .getMp3()
            .catch(() => {
              // Ignore errors during cleanup
            });
        } catch (error: unknown) {
          // Ignore errors during cleanup
        }
      }
    };
  }, [recorder, isRecording]);

  // Toggle recording on click instead of hold
  const handleClick = useCallback(() => {
    // Не позволяем кликать во время транскрибации
    if (isTranscribing) return;

    if (isRecording) {
      // Если запись идет, останавливаем
      handleStopRecording();
    } else {
      // Если записи нет, начинаем
      handleStartRecording();
    }
  }, [isRecording, isTranscribing, handleStartRecording, handleStopRecording]);

  return (
    <div
      className={`
        flex h-6 w-6 items-center justify-center rounded-full
        pr-[3px] transition-all duration-300 focus:outline-none
        focus:ring-0
        ${
          isRecording
            ? "animate-pulse cursor-pointer bg-green-500"
            : "cursor-pointer bg-transparent hover:bg-gray-100"
        }
        ${isTranscribing ? "cursor-default" : ""}
        ${disabled ? "cursor-not-allowed opacity-50" : ""}
      `}
      onClick={handleClick}
      role="button"
      tabIndex={disabled || isTranscribing ? -1 : 0}
    >
      {isTranscribing ? (
        <div className="flex h-5 w-5 items-center justify-center">
          <div className="h-5 w-5 animate-spin text-black-400">
            {progressSvg}
          </div>
        </div>
      ) : (
        <div className="flex h-5 w-5 items-center justify-center">
          <Icon24Mic
            className={`h-5 w-5 transition-colors ${
              isRecording ? "text-white" : "text-black-400"
            }`}
          />
        </div>
      )}
    </div>
  );
}
